import {
  Component,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
  viewChildren,
  ElementRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

interface TransferDetails {
  accountNumber: string;
  card: string;
  ifsc: string;
  fee: string;
  available: string;
}

interface SourceAccount {
  name: string;
  number: string;
  available: string;
  type: string;
}

@Component({
  selector: 'app-bank-account-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './bank-account-modal.html',
  styleUrls: ['./bank-account-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankAccountModal implements OnInit, OnDestroy {
  // Angular 21: Using output() instead of @Output()
  close = output<void>();

  // Angular 21: Using viewChildren() instead of @ViewChildren()
  pinInputs = viewChildren<ElementRef<HTMLInputElement>>('pinInput');

  // Angular 21: Using signals for reactive state
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  readonly step = signal<number>(1);
  readonly isProcessing = signal<boolean>(false);
  readonly showCancelConfirm = signal<boolean>(false);
  readonly isNoteInputVisible = signal<boolean>(false);
  readonly isAccountDropdownOpen = signal<boolean>(false);
  readonly recipientName = signal<string>('');
  readonly finalAmount = signal<string>('0.00');
  readonly canProceedStep1Signal = signal<boolean>(false);
  readonly canProceedStep2Signal = signal<boolean>(false);
  readonly apiError = signal<string | null>(null);

  form!: FormGroup;

  readonly sourceAccounts: SourceAccount[] = [
    { name: 'Credit Card 1', number: '**** 1234', available: '₹6,280,050.75', type: 'Credit Card' },
    { name: 'HDFC Bank', number: '**** 5678', available: '₹1,45,000.00', type: 'Bank Account' },
    { name: 'SBI Bank', number: '**** 9012', available: '₹50,000.00', type: 'Bank Account' }
  ];

  readonly selectedAccount = signal<SourceAccount>(this.sourceAccounts[0]);

  readonly transferDetails = signal<TransferDetails>({
    accountNumber: '',
    card: '**** 1234',
    ifsc: '',
    fee: '25.00',
    available: '₹6,280,050.75'
  });

  // Computed signals for derived values
  readonly recipientInitial = computed(() => {
    const name = this.recipientName();
    return name ? name.charAt(0).toUpperCase() : '';
  });

  readonly canProceedStep4 = computed(() => {
    return this.form?.get('pin')?.valid ?? false;
  });

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    // Subscribe to form status changes to update button states
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkStep1Validity();
      this.checkStep2Validity();
      if (this.form.get('pin')?.dirty) {
        this.apiError.set(null);
      }
    });

    // Subscribe to step 1 field changes
    this.form.get('recipientName')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkStep1Validity();
    });

    this.form.get('accountNumber')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkStep1Validity();
    });

    this.form.get('ifscCode')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.checkStep1Validity();

      // Auto-uppercase IFSC code
      if (val && typeof val === 'string') {
        const upperVal = val.toUpperCase();
        if (upperVal !== val) {
          this.form.get('ifscCode')?.setValue(upperVal, { emitEvent: false });
        }
      }
    });

    // Subscribe to amount changes to update finalAmount view and button state
    this.form.get('amount')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.checkStep2Validity();

      if (val) {
        const formatted = parseFloat(val).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        this.finalAmount.set(formatted);
      } else {
        this.finalAmount.set('0.00');
      }
    });

    // Initial check
    this.checkStep1Validity();
    this.checkStep2Validity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Step 1 Fields
      recipientName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(/^[a-zA-Z\s]+$/)
      ]],
      accountNumber: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(18),
        Validators.pattern(/^\d+$/)
      ]],
      ifscCode: ['', [
        Validators.required,
        Validators.pattern(/^[A-Z]{4}0[A-Z0-9]{6}$/)
      ]],
      amount: ['', [
        Validators.required,
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
        Validators.min(1)
      ]],
      pin: this.fb.array(Array(6).fill('').map(() => this.fb.control('', [
        Validators.required,
        Validators.pattern(/^\d$/)
      ])))
    });
  }

  // Helper method to check step 1 validity
  private checkStep1Validity(): void {
    if (!this.form) {
      this.canProceedStep1Signal.set(false);
      return;
    }

    const recipientName = this.form.get('recipientName');
    const accountNumber = this.form.get('accountNumber');
    const ifscCode = this.form.get('ifscCode');

    const isValid = !!(recipientName?.valid &&
      accountNumber?.valid &&
      ifscCode?.valid);
    this.canProceedStep1Signal.set(isValid);
  }

  // Helper method to check step 2 validity
  private checkStep2Validity(): void {
    if (!this.form) {
      this.canProceedStep2Signal.set(false);
      return;
    }

    const amountControl = this.form.get('amount');
    if (!amountControl?.valid) {
      this.canProceedStep2Signal.set(false);
      return;
    }

    const amount = parseFloat(amountControl.value || '0');
    this.canProceedStep2Signal.set(amount > 0);
  }

  getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (control && control.invalid && (control.touched || control.dirty)) {
      if (control.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (control.errors?.['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${control.errors?.['minlength'].requiredLength} characters`;
      }
      if (control.errors?.['maxlength']) {
        return `${this.getFieldLabel(fieldName)} must be at most ${control.errors?.['maxlength'].requiredLength} characters`;
      }
      if (control.errors?.['pattern']) {
        if (fieldName === 'recipientName') {
          return 'Recipient name must contain only letters and spaces';
        }
        if (fieldName === 'accountNumber') {
          return 'Account number must contain only digits';
        }
        if (fieldName === 'ifscCode') {
          return 'Invalid IFSC code format (e.g., HDFC0001234)';
        }
        if (fieldName === 'amount') {
          return 'Please enter a valid amount';
        }
        return `Invalid ${this.getFieldLabel(fieldName).toLowerCase()} format`;
      }
      if (control.errors?.['min']) {
        return 'Amount must be greater than 0';
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'recipientName': 'Recipient Name',
      'accountNumber': 'Account Number',
      'ifscCode': 'IFSC Code',
      'amount': 'Amount'
    };
    return labels[fieldName] || fieldName;
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  get pinControls(): AbstractControl[] {
    return (this.form.get('pin') as FormArray).controls;
  }

  toggleNoteInput(): void {
    this.isNoteInputVisible.update(value => !value);
  }

  toggleAccountDropdown(): void {
    this.isAccountDropdownOpen.update(value => !value);
  }

  selectAccount(account: SourceAccount): void {
    this.selectedAccount.set(account);
    this.isAccountDropdownOpen.set(false);
    this.transferDetails.update(details => ({
      ...details,
      card: account.number,
      available: account.available
    }));
  }

  nextStep(): void {
    const currentStep = this.step();

    if (currentStep === 1) {
      // Validate Step 1 - mark all as touched
      const s1Controls = ['recipientName', 'accountNumber', 'ifscCode'];
      s1Controls.forEach(c => {
        const control = this.form.get(c);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      // Re-check validity after marking as touched
      this.checkStep1Validity();

      // Only proceed if valid
      if (!this.canProceedStep1Signal()) {
        return;
      }

      // Sync data
      this.recipientName.set(this.form.get('recipientName')?.value || '');
      this.transferDetails.update(details => ({
        ...details,
        accountNumber: this.form.get('accountNumber')?.value || '',
        ifsc: this.form.get('ifscCode')?.value || ''
      }));
    }

    if (currentStep === 2) {
      const amountControl = this.form.get('amount');
      amountControl?.markAsTouched();
      amountControl?.updateValueAndValidity();

      // Re-check validity
      this.checkStep2Validity();

      if (!this.canProceedStep2Signal()) {
        return;
      }
    }

    if (currentStep === 3) {
      // Review step, just proceed
    }

    if (currentStep === 4) {
      const pinArray = this.form.get('pin') as FormArray;
      this.apiError.set(null);
      pinArray.markAsTouched();
      pinArray.controls.forEach(control => control.markAsTouched());

      if (pinArray.invalid) return;

      const pinValue = pinArray.value.join('');

      this.step.set(5);
      this.isProcessing.set(true);

      timer(1500)
        .pipe(
          finalize(() => {
            this.isProcessing.set(false);
          })
        )
        .subscribe(() => {
          if (pinValue === '000000') {
            this.step.set(4);
            this.apiError.set('Incorrect PIN. Please try again.');
            pinArray.reset();
            setTimeout(() => this.pinInputs()[0]?.nativeElement.focus());
          }
        });
      return;
    }

    if (currentStep < 5) {
      this.step.update(s => s + 1);
    } else {
      this.confirmCancel();
    }
  }

  prevStep(): void {
    if (this.step() > 1) {
      this.step.update(s => s - 1);
    }
  }

  onClose(): void {
    const currentStep = this.step();
    if (currentStep === 1 || currentStep === 5) {
      this.confirmCancel();
    } else {
      this.showCancelConfirm.set(true);
    }
  }

  confirmCancel(): void {
    this.close.emit();
  }

  closeCancelConfirm(): void {
    this.showCancelConfirm.set(false);
  }

  onPinInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && index < 5) {
      const inputs = this.pinInputs();
      if (inputs[index + 1]) {
        inputs[index + 1].nativeElement.focus();
      }
    }
  }

  onPinBackspace(index: number, event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const input = keyboardEvent.target as HTMLInputElement;
    const value = input.value;
    if (!value && index > 0) {
      const inputs = this.pinInputs();
      if (inputs[index - 1]) {
        inputs[index - 1].nativeElement.focus();
      }
    }
  }

  trackByFn(index: number): number {
    return index;
  }

  formatAmount(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^\d.]/g, '');

    // Prevent multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }

    const control = this.form.get('amount');
    if (control && value !== control.value) {
      control.setValue(value, { emitEvent: true });
    }
  }

  formatAccountNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Only digits

    const control = this.form.get('accountNumber');
    if (control && value !== control.value) {
      control.setValue(value, { emitEvent: true });
    }
  }
}